// src/ui-room.ts
import SunCalc from 'suncalc';
import {
  MapManager,
  SELF_COLOR,
  MAP_ORIENTATION_STORAGE_KEY,
  parseMapOrientation,
  type MapOrientation,
} from './map-manager';
import { isCarColor } from './car-colors';
import { DEFAULT_CAR_SHAPE, isCarShape } from './car-shapes';
import { nominatimSearchUrl, parseNominatimResults, type PlaceHit } from './place-search';
import { type MapStyle, MAP_STYLE_STORAGE_KEY } from './config';
import { GeoManager, thinTrailForBroadcast } from './geo-manager';
import { PeerManager } from './peer-manager';
import { ChatManager } from './chat-manager';
import { buildHandoffUrl, buildRoomInvite, renderInviteQr } from './share-room';
import type { RoomLaunchConfig } from './room-state';
import type { P2PMessage, LocationMessage, CheckWordMessage, HostInfoMessage, DestinationMessage, ChatMessage } from './protocol';

interface PeerInfo {
  peerId: string;
  name: string;
  lat: number;
  lng: number;
  speed: number | null;
  city: string;
  destination: string;
  destLat: number | null;
  destLng: number | null;
  isSelf: boolean;
}

/** Great-circle distance in kilometers between two lat/lng points */
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Format distance (km) into a short human string using miles */
function formatDistance(km: number): string {
  const mi = km * 0.621371;
  if (mi < 0.1) return '<0.1 mi';
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}

/** Format an ETA in minutes into a short human string */
function formatEta(minutes: number): string {
  if (!isFinite(minutes) || minutes <= 0) return '';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

export function renderRoom(
  container: HTMLElement,
  room: RoomLaunchConfig,
  onLeave: () => void,
): void {
  const roomCode = room.code;
  container.innerHTML = `
    <div class="flex flex-col h-screen overflow-hidden bg-tesla-bg" id="room-root">

      <!-- Top bar -->
      <div id="top-bar" class="glass flex items-center justify-between px-3 py-2 border-b flex-shrink-0 z-10 gap-2">
        <div class="flex items-center gap-2 min-w-0 flex-shrink">
          <div class="text-white font-semibold text-sm sm:text-lg tracking-[0.1em] sm:tracking-[0.18em] truncate">${escapeHtml(roomCode)}</div>
          ${room.isHost ? '<span class="text-[10px] bg-tesla-cyan text-white font-semibold rounded-full px-2 py-0.5 flex-shrink-0 tracking-wider">HOST</span>' : ''}
        </div>
        <div class="flex gap-1.5 sm:gap-2 items-center flex-shrink-0">
          <button id="people-btn" class="topbar-btn border border-white/15 text-tesla-muted hover:text-white rounded-full px-3 py-1.5 text-xs transition-colors">
            Waiting…
          </button>
          <button id="share-btn"
            class="topbar-btn border border-white/15 text-white rounded-full px-3 py-1.5 text-xs active:opacity-75">
            Share
          </button>
          <button id="pause-btn"
            class="topbar-btn border border-white/15 text-white rounded-full px-3 py-1.5 text-xs active:opacity-75">
            Pause
          </button>
          <button id="chat-btn"
            class="topbar-btn relative bg-tesla-cyan text-white font-semibold rounded-full px-3 py-1.5 text-xs active:opacity-75">
            Chat
            <span id="chat-unread-count" class="hidden absolute -top-2 -right-2 min-w-[1.25rem] h-5 px-1 rounded-full bg-white text-black text-[11px] font-bold flex items-center justify-center"></span>
          </button>
          <button id="leave-btn"
            class="topbar-btn bg-tesla-red/90 text-white rounded-full px-3 py-1.5 text-xs font-semibold active:opacity-75">
            Leave
          </button>
        </div>
      </div>

      <!-- Map area (map + all floating overlays) -->
      <div class="flex-1 relative overflow-hidden">

        <!-- Map fills full area -->
        <div id="map-container" class="absolute inset-0 z-0"></div>

        <!-- Status overlay -->
        <div id="status-overlay"
          class="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-20">
          <div class="text-center">
            <div class="text-white text-xl font-medium tracking-wide mb-2" id="status-text">Connecting…</div>
            <div class="text-tesla-muted text-sm" id="status-sub">Getting your location and joining the room</div>
          </div>
        </div>

        <!-- Geo-unavailable banner (non-blocking, dismissible) -->
        <div id="geo-banner"
          class="glass absolute top-2 left-1/2 -translate-x-1/2 z-30 border !border-yellow-600/60 text-white rounded-2xl px-4 py-3 text-sm max-w-md hidden">
          <div class="flex items-start gap-3">
            <div class="flex-1">
              <div class="font-semibold text-yellow-200 text-xs uppercase tracking-wider mb-1">Location Unavailable</div>
              <div id="geo-banner-text" class="text-white/90 text-xs leading-relaxed">Your browser cannot determine your location. You can still see other people on the map. Check System Settings on macOS or browser site permissions.</div>
            </div>
            <button id="geo-banner-close" class="text-yellow-400 text-lg leading-none px-1 flex-shrink-0 active:opacity-75">&times;</button>
          </div>
        </div>

        <!-- Error toast -->
        <div id="error-toast"
          class="absolute bottom-4 left-4 right-4 bg-red-900 border border-red-700 text-white rounded-xl px-4 py-3 text-sm z-40 hidden">
          <span id="error-text"></span>
        </div>

        <!-- North-up / heading-up (right side, above fit) -->
        <div id="orient-picker" class="glass map-control absolute bottom-52 right-3 z-10 border rounded-full p-0.5 flex flex-col gap-0.5">
          <button id="orient-north" class="orient-btn w-9 h-9 rounded-full flex items-center justify-center text-white"
            aria-label="North up" title="North up" data-orient="north">
            <span class="text-[11px] font-semibold tracking-wide">N</span>
          </button>
          <button id="orient-heading" class="orient-btn w-9 h-9 rounded-full flex items-center justify-center text-white"
            aria-label="Direction of travel up" title="Direction of travel" data-orient="heading">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3 L20 21 L12 17 L4 21 Z"/>
            </svg>
          </button>
        </div>

        <!-- Fit-everyone button (right side, above locate) -->
        <button id="fit-btn"
          class="glass map-control absolute bottom-40 right-3 z-10 border rounded-full w-10 h-10 flex items-center justify-center text-white active:opacity-75"
          aria-label="Fit everyone on screen" title="Fit everyone">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/>
          </svg>
        </button>

        <!-- Locate / follow (right side, above destination) -->
        <button id="locate-btn"
          class="glass map-control absolute bottom-28 right-3 z-10 border rounded-full w-10 h-10 flex items-center justify-center text-white active:opacity-75"
          aria-label="Keep me centered" title="Keep me centered">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
          </svg>
        </button>

        <!-- Map style picker (bottom-left, stacked above trail) -->
        <div id="style-picker" class="glass absolute bottom-14 left-3 z-10 border rounded-full px-1.5 py-1 flex gap-0.5 items-center">
          <button class="style-btn map-control px-2 py-1 rounded-full text-[10px] font-medium transition-colors" data-style="auto">Auto</button>
          <button class="style-btn map-control px-2 py-1 rounded-full text-[10px] font-medium transition-colors" data-style="light">Light</button>
          <button class="style-btn map-control px-2 py-1 rounded-full text-[10px] font-medium transition-colors" data-style="dark">Dark</button>
          <button class="style-btn map-control px-2 py-1 rounded-full text-[10px] font-medium transition-colors" data-style="satellite">Sat</button>
          <button class="style-btn map-control px-2 py-1 rounded-full text-[10px] font-medium transition-colors" data-style="outdoors">Out</button>
        </div>

        <!-- Trail controls (bottom-left) -->
        <div class="glass safe-bottom absolute bottom-3 left-3 z-10 border rounded-full px-3 py-1.5 flex gap-1.5 items-center">
          <span class="text-tesla-muted text-[10px] uppercase tracking-[0.15em]">Trail</span>
          <button class="trail-btn map-control text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white" data-mins="10">10m</button>
          <button class="trail-btn map-control text-[11px] px-2 py-0.5 rounded-full border border-tesla-cyan text-tesla-cyan" data-mins="30">30m</button>
          <button class="trail-btn map-control text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white" data-mins="60">60m</button>
        </div>

        <!-- Destination panel (bottom-right) -->
        <div id="dest-panel" class="glass safe-bottom absolute bottom-3 right-3 z-10 border rounded-2xl overflow-hidden max-w-[220px] sm:max-w-[280px]">
          <button id="dest-toggle" class="flex items-center gap-2 px-4 py-2 text-sm text-white w-full text-left active:opacity-75">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
              fill="currentColor" class="text-tesla-cyan flex-shrink-0">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span id="dest-label-btn" class="truncate">Set Destination</span>
          </button>
          <div id="dest-form" class="hidden border-t border-white/[0.08] px-3 pb-3 pt-2">
            <div class="flex gap-2">
              <input id="dest-input" type="text" placeholder="Place or address…"
                class="flex-1 min-w-0 bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-tesla-muted focus:outline-none focus:border-tesla-cyan"
                autocomplete="off" autocorrect="off" spellcheck="false"/>
              <button id="dest-set" class="bg-tesla-cyan text-white font-semibold px-4 py-2 rounded-lg text-sm flex-shrink-0 active:opacity-75">Search</button>
            </div>
            <div id="dest-results" class="hidden mt-2 max-h-48 overflow-y-auto rounded-lg border border-white/10"></div>
            <div id="dest-status" class="text-xs text-tesla-muted mt-1.5 hidden"></div>
            <button id="dest-clear" class="hidden w-full mt-2 text-sm text-tesla-red/90 border border-tesla-red/40 rounded-full py-1.5 active:opacity-75">
              Clear Destination
            </button>
          </div>
        </div>

        <!-- People panel (top-left floating) -->
        <div id="people-panel" class="glass hidden absolute top-2 left-3 right-3 sm:right-auto sm:w-64 z-30 border rounded-2xl shadow-2xl flex flex-col overflow-hidden" style="max-height:calc(100% - 1rem)">
          <div class="flex items-center justify-between px-4 py-3 border-b border-white/[0.08] flex-shrink-0">
            <span id="people-title" class="text-[11px] uppercase tracking-[0.2em] text-tesla-muted font-semibold">In This Room</span>
            <button id="people-close" class="text-tesla-muted text-2xl leading-none px-1 active:opacity-75">×</button>
          </div>
          <div id="people-list" class="overflow-y-auto flex-1 min-h-0"></div>
        </div>

        <!-- One line in the corner when chat is closed. Stays off the car in the middle. -->
        <button id="chat-alert" type="button"
          class="hidden absolute top-2 right-3 z-40 max-w-[15rem] glass border border-white/25 rounded-2xl px-3 py-2 text-left shadow-2xl active:opacity-75">
          <span id="chat-alert-kicker" class="block text-[10px] uppercase tracking-[0.16em] text-white font-semibold">New message</span>
          <span id="chat-alert-text" class="block text-sm text-white truncate"></span>
        </button>

        <!-- Chat floating panel (top-right, full-width on mobile) -->
        <div id="chat-container" class="glass hidden absolute top-2 left-3 right-3 sm:left-auto sm:w-96 z-30 border rounded-2xl shadow-2xl overflow-hidden flex flex-col"></div>

      </div>
    </div>
  `;

  // --- Map style ---
  const savedStyle = (localStorage.getItem(MAP_STYLE_STORAGE_KEY) ?? 'auto') as MapStyle;
  let currentStyle: MapStyle = savedStyle;

  /** Resolve 'auto' to a concrete tile style based on the user's local sunrise/sunset. */
  function resolveStyle(lat?: number, lng?: number): Exclude<MapStyle, 'auto'> {
    if (currentStyle !== 'auto') return currentStyle as Exclude<MapStyle, 'auto'>;
    try {
      const now = new Date();
      const useLat = lat ?? selfInfo?.lat ?? 37.7749;
      const useLng = lng ?? selfInfo?.lng ?? -122.4194;
      const times = SunCalc.getTimes(now, useLat, useLng);
      const ms = now.getTime();
      const isDaytime = ms >= times.sunrise.getTime() && ms <= times.sunset.getTime();
      return isDaytime ? 'light' : 'dark';
    } catch {
      return 'light';
    }
  }

  const mapManager = new MapManager('map-container', resolveStyle());
  mapManager.setOrientation(parseMapOrientation(localStorage.getItem(MAP_ORIENTATION_STORAGE_KEY)));
  // The user's chosen car color (validated); undefined = auto-assigned.
  const myColor = isCarColor(room.carColor) ? room.carColor : undefined;
  const myShape = isCarShape(room.carShape) ? room.carShape : DEFAULT_CAR_SHAPE;
  if (myColor) mapManager.setSelfColor(myColor);
  mapManager.setSelfShape(myShape);
  const chatUnreadCount = document.getElementById('chat-unread-count')!;
  const chatAlert = document.getElementById('chat-alert')!;
  const chatAlertText = document.getElementById('chat-alert-text')!;
  const chatAlertKicker = document.getElementById('chat-alert-kicker')!;
  const chatBtn = document.getElementById('chat-btn')!;
  let unreadCount = 0;

  function clearChatAlert(): void {
    unreadCount = 0;
    chatBtn.classList.remove('chat-attn');
    chatUnreadCount.classList.add('hidden');
    chatUnreadCount.textContent = '';
    chatAlert.classList.add('hidden');
    chatAlertText.textContent = '';
  }

  function showChatAlert(msg: ChatMessage): void {
    unreadCount += 1;
    chatUnreadCount.textContent = unreadCount > 9 ? '9+' : String(unreadCount);
    chatUnreadCount.classList.remove('hidden');
    chatBtn.classList.add('chat-attn');
    const who = msg.name?.trim() || 'Someone';
    const body = msg.text.replace(/\s+/g, ' ').trim();
    chatAlertKicker.textContent = unreadCount === 1 ? 'New message' : `${unreadCount} new messages`;
    chatAlertText.textContent = `${who}: ${body}`;
    chatAlert.classList.remove('hidden');
  }

  const chatManager = new ChatManager(
    'chat-container',
    sendChat,
    (msg) => {
      showChatAlert(msg);
    },
    // Color chat name labels with the sender's car color (known after their
    // first location message; default muted styling until then).
    (peerId) => {
      const c = mapManager.getPeerColor(peerId);
      return c === '#888888' ? '' : c;
    },
  );
  chatManager.setMyColor(myColor ?? SELF_COLOR);
  let paused = false;

  let lastLocationMsg: LocationMessage | null = null;
  let lastDestinationMsg: DestinationMessage | null = null;
  let hostPeerId: string | null = null;

  // Rolling chat history — held in peer memory, never sent to the server.
  // Capped at 50 messages and 1 hour. Replayed to any peer who joins mid-session.
  const HISTORY_MAX = 50;
  const HISTORY_TTL_MS = 60 * 60 * 1000;
  const chatHistory: ChatMessage[] = [];
  const seenMsgIds = new Set<string>();

  function msgId(msg: ChatMessage): string {
    return `${msg.peerId}:${msg.timestamp}`;
  }

  function addToHistory(msg: ChatMessage): void {
    const id = msgId(msg);
    if (seenMsgIds.has(id)) return;
    seenMsgIds.add(id);
    chatHistory.push(msg);
    const cutoff = Date.now() - HISTORY_TTL_MS;
    while (chatHistory.length > HISTORY_MAX || (chatHistory[0] && chatHistory[0].timestamp < cutoff)) {
      const pruned = chatHistory.shift();
      if (pruned) seenMsgIds.delete(msgId(pruned));
    }
  }
  const approvedPeers = new Set<string>();
  const peerCheckWords = new Map<string, string>();

  // People panel state
  const peerInfos = new Map<string, PeerInfo>();
  let selfInfo: PeerInfo | null = null;

  // Stale peer detection — tracks last received location timestamp per peer.
  // Used to dim markers and eventually auto-remove peers whose ICE connection
  // silently failed (no 'close' event, just frozen location).
  const peerLastSeen = new Map<string, number>();
  const STALE_WARN_MS = 90_000;    // dim marker + show "last seen" after 90s
  const STALE_REOPEN_MS = 45_000;  // silent channel: hang up and dial again
  const STALE_REMOVE_MS = 15 * 60_000; // drop the car only after a long gap
  let linkUp = false;

  const peerManager = new PeerManager(handlePeerMessage, handlePeerStatus, handlePeerOpen);
  const broadcastLocation = peerManager.locationBroadcaster();
  let hasLocation = false;
  let peerConnected = false;

  const geoManager = new GeoManager((update) => {
    if (paused) return;
    hasLocation = true;
    mapManager.updateSelf(update);
    const myId = peerManager.getMyId() ?? '';
    const msg: LocationMessage = {
      type: 'location',
      lat: update.lat,
      lng: update.lng,
      heading: update.heading,
      speed: update.speed,
      timestamp: update.timestamp,
      // Thinned for the wire; the local map still renders the full trail.
      trail: thinTrailForBroadcast(update.trail),
      peerId: myId,
      name: room.displayName || undefined,
      color: myColor,
      shape: myShape,
    };
    lastLocationMsg = msg;
    broadcastLocation(msg);
    maybeHideStatus();

    // Update self in people panel
    const prevCity = selfInfo?.city ?? '';
    const posChanged = !selfInfo || coordKey(update.lat, update.lng) !== coordKey(selfInfo.lat, selfInfo.lng);
    // Re-render the panel only when something it displays could have changed
    // (~11 m position granularity, 0.1 m/s speed). watchPosition can fire
    // ~1×/sec while parked, and an unconditional innerHTML rebuild would eat
    // taps and battery for no visible difference.
    const panelChanged =
      !selfInfo ||
      selfInfo.lat.toFixed(4) !== update.lat.toFixed(4) ||
      selfInfo.lng.toFixed(4) !== update.lng.toFixed(4) ||
      (selfInfo.speed ?? 0).toFixed(1) !== (update.speed ?? 0).toFixed(1);
    selfInfo = {
      peerId: myId,
      name: room.displayName || 'You',
      lat: update.lat,
      lng: update.lng,
      speed: update.speed,
      city: posChanged ? '' : prevCity,
      destination: selfInfo?.destination ?? '',
      destLat: selfInfo?.destLat ?? null,
      destLng: selfInfo?.destLng ?? null,
      isSelf: true,
    };
    if (posChanged) {
      reverseGeocodeAndUpdate('self', update.lat, update.lng);
    }
    if (panelChanged) schedulePeopleRender();
  });

  geoManager.start();
  peerManager.connect(roomCode);

  // Keep a phone screen awake for the trip. Tesla's browser usually stays on
  // while driving; a passenger phone does not. Unsupported browsers no-op.
  let screenLock: WakeLockSentinel | null = null;
  const requestScreenLock = (): void => {
    if (!('wakeLock' in navigator) || document.visibilityState !== 'visible') return;
    void navigator.wakeLock.request('screen').then(lock => {
      screenLock = lock;
    }).catch(() => {
      screenLock = null;
    });
  };
  const onVisible = (): void => requestScreenLock();
  requestScreenLock();
  document.addEventListener('visibilitychange', onVisible);

  // Coalesce people-panel rebuilds. With a big convoy, location messages
  // arrive several times per second and each used to trigger a full
  // innerHTML rebuild (plus listener reattachment). Skip entirely while the
  // panel is closed — it renders fresh on open — and batch to at most one
  // rebuild per 300 ms while it's visible.
  let peopleRenderTimer: number | null = null;
  function schedulePeopleRender(): void {
    const panel = document.getElementById('people-panel');
    if (!panel || panel.classList.contains('hidden')) return;
    if (peopleRenderTimer !== null) return;
    peopleRenderTimer = window.setTimeout(() => {
      peopleRenderTimer = null;
      renderPeopleList();
    }, 300);
  }

  /**
   * Drop all per-peer UI state. Callers re-render (updatePeopleBtn +
   * renderPeopleList) after — batched, so a sweep can forget several peers
   * with one render. Approval state (approvedPeers/peerCheckWords) is only
   * revoked when the data channel is actually gone: the check-word handshake
   * re-runs on the next conn 'open', so a reconnecting peer re-approves. A
   * peer forgotten while their channel is still open (stale sweep, pause)
   * must keep approval or the host would silently drop them forever.
   */
  function forgetPeer(peerId: string, revokeApproval: boolean): void {
    mapManager.removePeer(peerId);
    if (revokeApproval) {
      approvedPeers.delete(peerId);
      peerCheckWords.delete(peerId);
    }
    peerInfos.delete(peerId);
    peerLastSeen.delete(peerId);
  }

  // Periodically check for peers whose location has gone stale (silent ICE
  // failure — no 'close' event, location just stops updating).
  const staleCheckInterval = setInterval(() => {
    const now = Date.now();
    // Our own dead zone must not age everyone else out. Hold their clocks
    // for this tick so the gap is not counted against them.
    if (!navigator.onLine || !linkUp) {
      for (const [peerId, seen] of peerLastSeen) peerLastSeen.set(peerId, seen + 30_000);
      return;
    }
    let changed = false;
    for (const [peerId, lastSeen] of peerLastSeen) {
      const age = now - lastSeen;
      if (age > STALE_REMOVE_MS) {
        forgetPeer(peerId, false);
        changed = true;
      } else if (age > STALE_REOPEN_MS) {
        peerManager.reopen(peerId);
        if (age > STALE_WARN_MS) {
          mapManager.setPeerStale(peerId, true);
          changed = true;
        }
      }
    }
    if (changed) {
      updatePeopleBtn();
      schedulePeopleRender();
    }
  }, 30_000);

  // Wire up DOM events
  document.getElementById('leave-btn')!.addEventListener('click', () => {
    if (!confirm('Leave room?\n\nYour location stops broadcasting and chat history is cleared from your device. Nothing is stored on our servers — history only lives in the memory of connected peers.')) return;
    teardown();
    onLeave();
  });

  document.getElementById('pause-btn')!.addEventListener('click', () => {
    paused = !paused;
    const btn = document.getElementById('pause-btn')!;
    btn.textContent = paused ? 'Resume' : 'Pause';
    btn.classList.toggle('bg-yellow-700', paused);
    peerManager.broadcast({
      type: 'pause',
      paused,
      peerId: peerManager.getMyId() ?? '',
    });
  });

  function openChat(): void {
    document.getElementById('chat-container')!.classList.remove('hidden');
    clearChatAlert();
  }

  chatBtn.addEventListener('click', () => {
    const panel = document.getElementById('chat-container')!;
    panel.classList.toggle('hidden');
    clearChatAlert();
  });
  chatAlert.addEventListener('click', openChat);

  function syncFollowButton(): void {
    const btn = document.getElementById('locate-btn');
    if (!btn) return;
    const on = mapManager.isFollowing();
    btn.classList.toggle('border-tesla-cyan', on);
    btn.classList.toggle('text-tesla-cyan', on);
  }

  function syncOrientButtons(): void {
    const current = mapManager.getOrientation();
    document.querySelectorAll<HTMLElement>('.orient-btn').forEach(btn => {
      const active = btn.dataset.orient === current;
      btn.classList.toggle('bg-tesla-cyan', active);
      btn.classList.toggle('text-white', active);
      btn.classList.toggle('text-tesla-muted', !active);
    });
  }

  mapManager.setOnFollowChange(() => {
    syncFollowButton();
    syncOrientButtons();
    try {
      localStorage.setItem(MAP_ORIENTATION_STORAGE_KEY, mapManager.getOrientation());
    } catch {
      /* private mode */
    }
  });
  syncFollowButton();
  syncOrientButtons();

  document.getElementById('locate-btn')!.addEventListener('click', () => {
    mapManager.centerOnSelf();
    syncFollowButton();
  });

  document.getElementById('fit-btn')!.addEventListener('click', () => {
    mapManager.fitAll();
    syncFollowButton();
  });

  document.getElementById('orient-north')!.addEventListener('click', () => {
    applyOrientation('north');
  });
  document.getElementById('orient-heading')!.addEventListener('click', () => {
    applyOrientation('heading');
  });

  function applyOrientation(orientation: MapOrientation): void {
    mapManager.setOrientation(orientation);
    try {
      localStorage.setItem(MAP_ORIENTATION_STORAGE_KEY, orientation);
    } catch {
      /* private mode */
    }
    syncOrientButtons();
    syncFollowButton();
  }

  // --- Style picker ---
  function applyStyle(style: Exclude<MapStyle, 'auto'>): void {
    mapManager.setStyle(style);
    document.querySelectorAll<HTMLElement>('.style-btn').forEach(btn => {
      const active = btn.dataset.style === currentStyle;
      btn.classList.toggle('bg-tesla-cyan', active);
      btn.classList.toggle('text-white', active);
      btn.classList.toggle('font-semibold', active);
      btn.classList.toggle('text-tesla-muted', !active);
    });
  }

  function refreshAutoStyle(lat?: number, lng?: number): void {
    if (currentStyle === 'auto') applyStyle(resolveStyle(lat, lng));
  }

  // Apply saved style immediately and highlight active button.
  applyStyle(resolveStyle());

  document.querySelectorAll<HTMLElement>('.style-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      currentStyle = (btn.dataset.style ?? 'auto') as MapStyle;
      localStorage.setItem(MAP_STYLE_STORAGE_KEY, currentStyle);
      applyStyle(resolveStyle(selfInfo?.lat, selfInfo?.lng));
    });
  });

  // Re-evaluate auto mode every 10 minutes (handles dawn/dusk mid-drive).
  const styleInterval = setInterval(() => {
    refreshAutoStyle(selfInfo?.lat, selfInfo?.lng);
  }, 10 * 60 * 1000);

  document.getElementById('people-btn')!.addEventListener('click', () => {
    const panel = document.getElementById('people-panel')!;
    const opening = panel.classList.contains('hidden');
    panel.classList.toggle('hidden');
    // Render fresh on open — updates are skipped while the panel is closed.
    if (opening) renderPeopleList();
  });
  document.getElementById('people-close')!.addEventListener('click', () => {
    document.getElementById('people-panel')!.classList.add('hidden');
  });

  document.getElementById('share-btn')!.addEventListener('click', () => {
    openShareModal();
  });

  function openShareModal(): void {
    document.getElementById('share-modal')?.remove();
    const check = room.checkWord?.trim() ?? '';
    const handoffUrl = buildHandoffUrl(roomCode, { checkWord: check });
    const qr = renderInviteQr(handoffUrl);
    const modal = document.createElement('div');
    modal.id = 'share-modal';
    modal.className = 'fixed inset-0 z-50 overflow-y-auto bg-black/80';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'share-modal-title');
    modal.innerHTML = `
      <div class="min-h-full flex items-center justify-center p-4">
      <div class="bg-tesla-surface border border-white/10 rounded-2xl p-6 w-full max-w-sm text-center">
        <h2 id="share-modal-title" class="text-white text-lg font-medium">Share this room</h2>
        <p class="text-white text-xl mt-3 tracking-[0.16em]">${escapeHtml(roomCode)}</p>
        ${check ? `<p class="text-tesla-muted text-sm mt-1">Check word: ${escapeHtml(check)}</p>` : ''}
        <div class="mx-auto mt-4 bg-white rounded-xl p-3 w-fit [&_svg]:w-60 [&_svg]:h-60">${qr}</div>
        <p class="text-tesla-muted text-sm mt-4">Scan with your phone to join, or to pass the invite along.</p>
        <button id="share-copy" class="mt-4 w-full border border-white/15 text-white rounded-full py-3 active:opacity-75">
          Copy invite link
        </button>
        <p id="share-copy-status" class="text-tesla-muted text-sm mt-2 min-h-[20px]"></p>
        <button id="share-close" class="w-full text-tesla-muted rounded-full py-3 active:opacity-75">Close</button>
      </div>
      </div>`;
    document.getElementById('room-root')!.appendChild(modal);

    const scrim = modal.firstElementChild;
    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target === scrim) modal.remove();
    });
    document.getElementById('share-close')!.addEventListener('click', () => modal.remove());
    document.getElementById('share-copy')!.addEventListener('click', async () => {
      const status = document.getElementById('share-copy-status');
      if (!status) return;
      const invite = buildRoomInvite(roomCode, { checkWord: check });
      try {
        await navigator.clipboard.writeText(`${invite.text}\n${invite.url}`);
        status.textContent = 'Invite copied.';
      } catch {
        status.textContent = 'Could not copy. Scan the code instead.';
      }
    });
  }

  document.querySelectorAll('.trail-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const mins = parseInt((e.currentTarget as HTMLElement).dataset.mins ?? '30', 10) as 10 | 30 | 60;
      mapManager.setTrailWindow(mins);
      document.querySelectorAll('.trail-btn').forEach(b => {
        const active = b === e.currentTarget;
        b.classList.toggle('border-tesla-cyan', active);
        b.classList.toggle('text-tesla-cyan', active);
        b.classList.toggle('border-tesla-border', !active);
        b.classList.toggle('text-white', !active);
      });
    });
  });

  const onGeoError = ((e: Event) => {
    const detail = (e as CustomEvent<string>).detail;
    showStatus(true, 'Waiting for location…', detail);
  }) as EventListener;

  const onGeoGaveUp = (() => {
    showStatus(false);
    const banner = document.getElementById('geo-banner');
    if (banner) banner.classList.remove('hidden');
  }) as EventListener;

  const onPeerLeft = ((e: Event) => {
    const peerId = (e as CustomEvent<string>).detail;
    // A blip closes the data channel. Keep the car where it was and let the
    // redial bring the next fix. A real departure ages out later.
    if (peerLastSeen.has(peerId)) mapManager.setPeerStale(peerId, true);
    schedulePeopleRender();
  }) as EventListener;

  document.addEventListener('geo:error', onGeoError);
  document.addEventListener('geo:gave-up', onGeoGaveUp);
  document.addEventListener('peer:left', onPeerLeft);

  document.getElementById('geo-banner-close')!.addEventListener('click', () => {
    document.getElementById('geo-banner')?.classList.add('hidden');
  });

  function handlePeerMessage(msg: P2PMessage, fromPeerId: string): void {
    if (msg.type === 'location') {
      if (room.isHost && room.checkWord && !approvedPeers.has(msg.peerId)) return;
      mapManager.updatePeer(msg);
      // Record fresh timestamp and clear any stale styling.
      peerLastSeen.set(msg.peerId, Date.now());
      mapManager.setPeerStale(msg.peerId, false);

      const existing = peerInfos.get(msg.peerId);
      const posChanged = !existing || coordKey(msg.lat, msg.lng) !== coordKey(existing.lat, existing.lng);
      peerInfos.set(msg.peerId, {
        peerId: msg.peerId,
        name: msg.name || existing?.name || msg.peerId.slice(0, 8),
        lat: msg.lat,
        lng: msg.lng,
        speed: msg.speed,
        city: posChanged ? '' : (existing?.city ?? ''),
        destination: existing?.destination ?? '',
        destLat: existing?.destLat ?? null,
        destLng: existing?.destLng ?? null,
        isSelf: false,
      });
      if (posChanged) {
        reverseGeocodeAndUpdate(msg.peerId, msg.lat, msg.lng);
      }
      updatePeopleBtn();
      schedulePeopleRender();
    } else if (msg.type === 'destination') {
      const info = peerInfos.get(msg.peerId);
      if (msg.lat !== null && msg.lng !== null) {
        mapManager.updateDestination(msg.peerId, msg.lat, msg.lng, msg.label, mapManager.getPeerColor(msg.peerId));
        if (info) {
          info.destination = msg.label;
          info.destLat = msg.lat;
          info.destLng = msg.lng;
          schedulePeopleRender();
        }
      } else {
        mapManager.clearDestination(msg.peerId);
        if (info) {
          info.destination = '';
          info.destLat = null;
          info.destLng = null;
          schedulePeopleRender();
        }
      }
    } else if (msg.type === 'chat') {
      addToHistory(msg);
      chatManager.receive(msg);
      if (msg.id && msg.peerId !== (peerManager.getMyId() ?? '')) {
        peerManager.sendToPeer(msg.peerId, {
          type: 'chat-ack',
          messageId: msg.id,
          peerId: peerManager.getMyId() ?? '',
        });
      }
      if (msg.name) {
        const info = peerInfos.get(msg.peerId);
        if (info && info.name !== msg.name) {
          info.name = msg.name;
          schedulePeopleRender();
        }
      }
    } else if (msg.type === 'chat-ack') {
      chatManager.noteAck(msg.messageId, fromPeerId);
    } else if (msg.type === 'room-history') {
      // Replay messages this peer missed — dedup via seenMsgIds.
      for (const m of msg.messages) {
        if (!seenMsgIds.has(msgId(m))) {
          addToHistory(m);
          chatManager.receive(m, false);
          if (m.name) {
            const info = peerInfos.get(m.peerId);
            if (info && info.name !== m.name) {
              info.name = m.name;
              schedulePeopleRender();
            }
          }
        }
      }
    } else if (msg.type === 'pause' && msg.paused) {
      forgetPeer(msg.peerId, false);
      updatePeopleBtn();
      schedulePeopleRender();
    } else if (msg.type === 'host-info') {
      handleHostInfo(msg);
    } else if (msg.type === 'check-word') {
      handleCheckWord(msg);
    } else if (
      msg.type === 'kick' &&
      msg.peerId === peerManager.getMyId() &&
      (!hostPeerId || fromPeerId === hostPeerId)
    ) {
      alert(msg.reason);
      teardown();
      onLeave();
    }
  }

  function handlePeerStatus(status: string, detail?: string): void {
    if (status === 'connected') {
      peerConnected = true;
      linkUp = true;
      const myId = peerManager.getMyId() ?? '';
      chatManager.setMyPeerId(myId);
      chatManager.setMyName(room.displayName ?? '');
      if (room.isHost) {
        hostPeerId = myId;
        broadcastHostInfo();
        approvedPeers.add(myId);
      }
      maybeHideStatus();
    } else if (status === 'connecting') {
      showStatus(true, 'Connecting…', 'Joining the room');
    } else if (status === 'disconnected') {
      linkUp = false;
    } else if (status === 'error') {
      showToast(detail ?? 'Connection error. Check your network.', 'error');
      showStatus(false);
    }
  }

  function handlePeerOpen(peerId: string): void {
    if (room.isHost) broadcastHostInfo();
    broadcastCheckWord();
    if (lastLocationMsg) {
      peerManager.broadcast({
        ...lastLocationMsg,
        peerId: peerManager.getMyId() ?? lastLocationMsg.peerId,
      });
    }
    if (lastDestinationMsg) {
      peerManager.broadcast(lastDestinationMsg);
    }
    // Send rolling chat history so the new peer can catch up without a server.
    if (chatHistory.length > 0) {
      peerManager.sendToPeer(peerId, {
        type: 'room-history',
        messages: [...chatHistory],
      });
    }
  }

  function sendChat(text: string, name: string | undefined, id: string): { attempted: number; sent: number } {
    const msg: ChatMessage = {
      type: 'chat',
      text,
      timestamp: Date.now(),
      peerId: peerManager.getMyId() ?? '',
      id,
      ...(name ? { name } : {}),
    };
    const receipt = peerManager.broadcast(msg);
    addToHistory(msg);
    return receipt;
  }

  function updatePeopleBtn(): void {
    // Count peers visible in the people panel, not raw data channels — a
    // zombie connection (silent ICE death, or a peer the stale sweep removed)
    // would otherwise keep the count inflated indefinitely.
    const n = peerInfos.size;
    document.getElementById('people-btn')!.textContent =
      n === 0 ? 'Waiting…' : `${n} other${n === 1 ? '' : 's'}`;
  }

  function handleHostInfo(msg: HostInfoMessage): void {
    hostPeerId = msg.hostPeerId;
    room.checkWord = room.checkWord ?? msg.checkWord ?? undefined;
  }

  function handleCheckWord(msg: CheckWordMessage): void {
    if (!room.isHost) return;
    peerCheckWords.set(msg.peerId, msg.checkWord ?? '');
    if (!room.checkWord || msg.checkWord === room.checkWord) {
      approvedPeers.add(msg.peerId);
    } else {
      kickPeer(msg.peerId, `Removed by host: check word "${msg.checkWord ?? ''}" did not match.`);
    }
    schedulePeopleRender();
  }

  function broadcastHostInfo(): void {
    const myId = peerManager.getMyId();
    if (!room.isHost || !myId) return;
    peerManager.broadcast({ type: 'host-info', hostPeerId: myId, checkWord: room.checkWord ?? null });
  }

  function broadcastCheckWord(): void {
    const myId = peerManager.getMyId();
    if (!myId) return;
    peerManager.broadcast({ type: 'check-word', peerId: myId, checkWord: room.checkWord ?? null });
  }

  function kickPeer(peerId: string, reason: string): void {
    peerManager.sendToPeer(peerId, { type: 'kick', peerId, reason });
    forgetPeer(peerId, true);
    updatePeopleBtn();
    schedulePeopleRender();
  }

  function renderPeopleList(): void {
    const list = document.getElementById('people-list');
    if (!list) return;

    // Self pinned on top, everyone else alphabetical — with a big room an
    // insertion-ordered list is unscannable.
    const entries: PeerInfo[] = [];
    if (selfInfo) entries.push(selfInfo);
    entries.push(
      ...[...peerInfos.values()].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    );

    const title = document.getElementById('people-title');
    if (title) {
      title.textContent = entries.length > 0 ? `In This Room (${entries.length})` : 'In This Room';
    }

    if (entries.length === 0) {
      list.innerHTML = '<div class="px-4 py-4 text-tesla-muted text-sm text-center">No one here yet.</div>';
      return;
    }

    const now = Date.now();
    list.innerHTML = entries.map(info => {
      const color = info.isSelf ? (myColor ?? SELF_COLOR) : mapManager.getPeerColor(info.peerId);
      const canKick = room.isHost && !info.isSelf;
      const lastSeen = !info.isSelf ? peerLastSeen.get(info.peerId) : undefined;
      const isStale = lastSeen !== undefined && now - lastSeen > STALE_WARN_MS;
      // "How far back is she?" is the convoy question — show each person's
      // distance from you next to their city.
      const fromYou = !info.isSelf && selfInfo
        ? `${formatDistance(haversineKm(selfInfo.lat, selfInfo.lng, info.lat, info.lng))} away`
        : '';
      const cityLine = isStale
        ? `Last seen ${formatEta((now - lastSeen) / 60_000) || 'just now'} ago`
        : [info.city || 'Getting location…', fromYou].filter(Boolean).join(' · ');
      let destLine = '';
      if (info.destination && info.destLat !== null && info.destLng !== null) {
        const km = haversineKm(info.lat, info.lng, info.destLat, info.destLng);
        const distStr = formatDistance(km);
        // Speed in m/s → km/h. Need >1 m/s (~2 mph) to consider moving.
        const speedKmh = info.speed && info.speed > 1 ? info.speed * 3.6 : 0;
        const etaStr = speedKmh > 0 ? formatEta((km / speedKmh) * 60) : '';
        const meta = etaStr ? `${distStr} · ${etaStr}` : distStr;
        destLine = `
          <div class="text-xs text-tesla-cyan mt-0.5 truncate">→ ${escapeHtml(info.destination)}</div>
          <div class="text-[10px] text-tesla-muted mt-0.5">${escapeHtml(meta)}</div>`;
      }
      return `
        <div class="people-row flex items-center gap-3 px-4 py-3 border-b border-white/[0.06] last:border-0 cursor-pointer hover:bg-black/20 active:bg-black/30 transition-colors"
          data-lat="${info.lat}" data-lng="${info.lng}">
          <div class="w-2.5 h-2.5 rounded-full flex-shrink-0${isStale ? ' opacity-40' : ''}" style="background:${color}"></div>
          <div class="flex-1 min-w-0">
            <div class="text-white text-sm font-medium truncate${isStale ? ' opacity-60' : ''}">${escapeHtml(info.name)}</div>
            <div class="text-xs ${isStale ? 'text-yellow-500' : 'text-tesla-muted'} mt-0.5">${escapeHtml(cityLine)}</div>
            ${destLine}
          </div>
          ${canKick ? `<button class="kick-btn flex-shrink-0 text-xs text-red-400 border border-red-800 rounded px-2 py-1 active:opacity-75" data-peer-id="${escapeAttr(info.peerId)}">Kick</button>` : ''}
        </div>`;
    }).join('');

    list.querySelectorAll<HTMLElement>('.people-row').forEach(row => {
      row.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.kick-btn')) return;
        const lat = parseFloat(row.dataset.lat!);
        const lng = parseFloat(row.dataset.lng!);
        if (!isNaN(lat) && !isNaN(lng)) {
          mapManager.panTo(lat, lng);
          document.getElementById('people-panel')!.classList.add('hidden');
        }
      });
    });

    list.querySelectorAll<HTMLElement>('.kick-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const peerId = (btn as HTMLButtonElement).dataset.peerId;
        if (peerId) kickPeer(peerId, 'Removed by host.');
      });
    });
  }

  function reverseGeocodeAndUpdate(key: string, lat: number, lng: number): void {
    void reverseGeocode(lat, lng).then(city => {
      if (key === 'self') {
        if (selfInfo) { selfInfo.city = city; schedulePeopleRender(); }
      } else {
        const info = peerInfos.get(key);
        if (info) { info.city = city; schedulePeopleRender(); }
      }
    });
  }

  function maybeHideStatus(): void {
    if (peerConnected && hasLocation) {
      showStatus(false);
      document.getElementById('geo-banner')?.classList.add('hidden');
    }
  }

  function showStatus(visible: boolean, title?: string, sub?: string): void {
    const overlay = document.getElementById('status-overlay');
    if (!overlay) return;
    overlay.classList.toggle('hidden', !visible);
    if (title) { const el = document.getElementById('status-text'); if (el) el.textContent = title; }
    if (sub) { const el = document.getElementById('status-sub'); if (el) el.textContent = sub; }
  }

  function showToast(msg: string, tone: 'error' | 'info'): void {
    const toast = document.getElementById('error-toast');
    const text = document.getElementById('error-text');
    if (!toast || !text) return;
    toast.classList.remove('bg-red-900', 'border-red-700', 'bg-tesla-surface', 'border-tesla-cyan');
    toast.classList.add(tone === 'info' ? 'bg-tesla-surface' : 'bg-red-900', tone === 'info' ? 'border-tesla-cyan' : 'border-red-700');
    text.textContent = msg;
    toast.classList.remove('hidden');
    setTimeout(() => toast.classList.add('hidden'), 5000);
  }

  // --- Destination panel ---
  const destPanel = document.getElementById('dest-panel')!;
  const destToggle = document.getElementById('dest-toggle')!;
  const destForm = document.getElementById('dest-form')!;
  const destInput = document.getElementById('dest-input') as HTMLInputElement;
  const destSetBtn = document.getElementById('dest-set')!;
  const destClearBtn = document.getElementById('dest-clear')!;
  const destStatus = document.getElementById('dest-status')!;
  const destResults = document.getElementById('dest-results')!;
  const destLabelBtn = document.getElementById('dest-label-btn')!;

  function setDestFormOpen(open: boolean): void {
    destForm.classList.toggle('hidden', !open);
    destPanel.classList.toggle('dest-editing', open);
    if (open) destInput.focus();
    else destInput.blur();
  }

  destToggle.addEventListener('click', () => {
    setDestFormOpen(destForm.classList.contains('hidden'));
  });

  function hideDestResults(): void {
    destResults.replaceChildren();
    destResults.classList.add('hidden');
  }

  function applyDestination(result: { lat: number; lng: number; label: string }): void {
    const myId = peerManager.getMyId() ?? '';
    const msg: DestinationMessage = { type: 'destination', peerId: myId, lat: result.lat, lng: result.lng, label: result.label };
    lastDestinationMsg = msg;
    mapManager.updateDestination(myId, result.lat, result.lng, result.label, myColor ?? SELF_COLOR);
    peerManager.broadcast(msg);
    destLabelBtn.textContent = result.label;
    hideDestResults();
    setDestFormOpen(false);
    destClearBtn.classList.remove('hidden');
    destStatus.classList.add('hidden');
    destInput.value = '';
    if (selfInfo) {
      selfInfo.destination = result.label;
      selfInfo.destLat = result.lat;
      selfInfo.destLng = result.lng;
      schedulePeopleRender();
    }
  }

  function showDestResults(hits: PlaceHit[]): void {
    destResults.replaceChildren();
    if (hits.length === 0) {
      destResults.classList.add('hidden');
      return;
    }
    for (const hit of hits) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dest-hit block w-full text-left px-3 py-2 border-b border-white/10 last:border-0 active:bg-white/10';
      const title = document.createElement('div');
      title.className = 'text-sm text-white truncate';
      title.textContent = hit.label;
      button.appendChild(title);
      if (hit.detail) {
        const detail = document.createElement('div');
        detail.className = 'text-[11px] text-tesla-muted truncate';
        detail.textContent = hit.detail;
        button.appendChild(detail);
      }
      button.addEventListener('click', () => applyDestination(hit));
      destResults.appendChild(button);
    }
    destResults.classList.remove('hidden');
  }

  async function searchDestination(): Promise<void> {
    const query = destInput.value.trim();
    if (query.length < 2) return;
    destStatus.textContent = 'Searching…';
    destStatus.classList.remove('hidden');
    destSetBtn.setAttribute('disabled', '');
    hideDestResults();
    try {
      const bias = selfInfo ? { lat: selfInfo.lat, lng: selfInfo.lng } : undefined;
      let hits = bias ? await searchPlaces(query, bias) : [];
      let widened = false;
      if (hits.length === 0) {
        hits = await searchPlaces(query);
        widened = bias !== undefined;
      }
      if (hits.length === 0) {
        destStatus.textContent = 'Nothing matched. Try a city or a more specific name.';
        return;
      }
      if (widened) destStatus.textContent = 'Nothing nearby. Showing other matches.';
      else destStatus.classList.add('hidden');
      showDestResults(hits);
    } catch {
      destStatus.textContent = 'Network error. Check your connection.';
    } finally {
      destSetBtn.removeAttribute('disabled');
    }
  }

  destSetBtn.addEventListener('click', () => void searchDestination());
  destInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') void searchDestination(); });
  destInput.addEventListener('input', () => hideDestResults());

  destClearBtn.addEventListener('click', () => {
    const myId = peerManager.getMyId() ?? '';
    const msg: DestinationMessage = { type: 'destination', peerId: myId, lat: null, lng: null, label: '' };
    lastDestinationMsg = null;
    mapManager.clearDestination(myId);
    peerManager.broadcast(msg);
    destLabelBtn.textContent = 'Set Destination';
    destClearBtn.classList.add('hidden');
    setDestFormOpen(false);
    if (selfInfo) {
      selfInfo.destination = '';
      selfInfo.destLat = null;
      selfInfo.destLng = null;
      schedulePeopleRender();
    }
  });

  function teardown(): void {
    document.removeEventListener('visibilitychange', onVisible);
    void screenLock?.release().catch(() => {});
    screenLock = null;
    clearInterval(styleInterval);
    clearInterval(staleCheckInterval);
    if (peopleRenderTimer !== null) clearTimeout(peopleRenderTimer);
    geoManager.stop();
    peerManager.disconnect();
    mapManager.destroy();
    document.removeEventListener('geo:error', onGeoError);
    document.removeEventListener('geo:gave-up', onGeoGaveUp);
    document.removeEventListener('peer:left', onPeerLeft);
  }
}

// Nominatim's usage policy is max 1 request/second per client. A room full of
// peers appearing at once used to fire a reverse-geocode burst that risks
// getting throttled or banned mid-trip — serialize every Nominatim call
// through this queue instead.
let _nominatimQueue: Promise<unknown> = Promise.resolve();
let _nominatimLastCall = 0;
function nominatimFetch(url: string): Promise<Response> {
  const run = _nominatimQueue.then(async () => {
    const wait = _nominatimLastCall + 1_100 - Date.now();
    if (wait > 0) await new Promise(r => setTimeout(r, wait));
    _nominatimLastCall = Date.now();
    return fetch(url, { headers: { Accept: 'application/json' } });
  });
  _nominatimQueue = run.catch(() => {});
  return run;
}

/** Up to five places, biased toward the car when we know where it is. */
async function searchPlaces(query: string, bias?: { lat: number; lng: number }): Promise<PlaceHit[]> {
  const res = await nominatimFetch(nominatimSearchUrl(query, bias));
  if (!res.ok) throw new Error(`nominatim ${res.status}`);
  return parseNominatimResults(await res.json());
}

/** Nominatim reverse geocode — cached by ~11km grid cell, deduped in flight */
const _revCache = new Map<string, string>();
const _revPending = new Map<string, Promise<string>>();
async function reverseGeocode(lat: number, lng: number): Promise<string> {
  const key = coordKey(lat, lng);
  if (_revCache.has(key)) return _revCache.get(key)!;
  // Convoys cluster: many peers land in the same ~11km cell at the same
  // moment. Share one request per cell instead of firing one per peer.
  const pending = _revPending.get(key);
  if (pending) return pending;
  const promise = (async () => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`;
      const res = await nominatimFetch(url);
      const data = await res.json() as { address?: { city?: string; town?: string; village?: string; county?: string; state?: string; state_code?: string } };
      const place = data.address?.city ?? data.address?.town ?? data.address?.village ?? data.address?.county ?? '';
      const state = data.address?.state_code ?? data.address?.state ?? '';
      const result = [place, state].filter(Boolean).join(', ') || 'Unknown location';
      _revCache.set(key, result);
      return result;
    } catch {
      return 'Unknown location';
    } finally {
      _revPending.delete(key);
    }
  })();
  _revPending.set(key, promise);
  return promise;
}

function coordKey(lat: number, lng: number): string {
  return `${lat.toFixed(1)},${lng.toFixed(1)}`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(text: string): string {
  return escapeHtml(text);
}
